import { useState, useEffect, useRef } from "react";

export function AntonymQuiz({ words, onComplete, onSaveAndExit, onExit, initialIdx = 0, initialResults = [], initialStartTime }) {
  const [phase, setPhase] = useState("loading"); // loading | quiz | insufficient | error
  const [validWords, setValidWords] = useState([]);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [currentChoices, setCurrentChoices] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const antCacheRef = useRef({});
  const resultsRef = useRef([]);
  const t0Ref = useRef(Date.now());

  // Fix 4: try rel_ant first, fall back to rel_jjb if empty
  async function fetchAntonymsWithFallback(word) {
    try {
      const res = await fetch(`https://api.datamuse.com/words?rel_ant=${encodeURIComponent(word)}&max=10`);
      const data = await res.json();
      if (data.length > 0) return data.map(w => w.word);
      const res2 = await fetch(`https://api.datamuse.com/words?rel_jjb=${encodeURIComponent(word)}&max=10`);
      const data2 = await res2.json();
      return data2.map(w => w.word);
    } catch { return []; }
  }

  useEffect(() => {
    prefetchAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function prefetchAll() {
    try {
      await Promise.all(words.map(async w => {
        const ants = await fetchAntonymsWithFallback(w.word);
        antCacheRef.current[w.id] = ants;
      }));
      const valid = words.filter(w => (antCacheRef.current[w.id] || []).length > 0);
      if (valid.length < 4) {
        setPhase("insufficient");
        return;
      }
      setValidWords(valid);
      const startIdx = initialIdx || 0;
      setCurrentChoices(buildChoices(valid[startIdx], valid));
      if (initialResults?.length) resultsRef.current = initialResults;
      t0Ref.current = (initialResults?.length && initialStartTime) ? initialStartTime : Date.now();
      setIdx(startIdx);
      setPhase("quiz");
    } catch {
      setErrorMsg("Something went wrong loading the quiz. Please try again.");
      setPhase("error");
    }
  }

  function buildChoices(targetWord, pool) {
    const correctAnts = antCacheRef.current[targetWord.id] || [];
    const correctAnt = correctAnts[0];
    // Try to get distractors from antonyms of other words
    const distractors = pool
      .filter(w => w.id !== targetWord.id)
      .sort(() => Math.random() - 0.5)
      .flatMap(w => {
        const a = antCacheRef.current[w.id] || [];
        return a.length && !correctAnts.includes(a[0]) ? [a[0]] : [];
      })
      .slice(0, 3);
    // Fill remaining slots with random words from the pool (Fix 3)
    const allWords = pool.map(w => w.word);
    let padIdx = 0;
    while (distractors.length < 3 && padIdx < allWords.length) {
      const candidate = allWords[padIdx];
      if (candidate !== targetWord.word && !distractors.includes(candidate) && candidate !== correctAnt) {
        distractors.push(candidate);
      }
      padIdx++;
    }
    const options = [
      { text: correctAnt, correct: true },
      ...distractors.slice(0, 3).map(d => ({ text: d, correct: false })),
    ];
    options.sort(() => Math.random() - 0.5);
    return { options };
  }

  function handleSaveAndExit() {
    const QUIZ_COUNT = validWords.length;
    const saveIdx = selected !== null ? idx + 1 : idx;
    if (saveIdx >= QUIZ_COUNT) { onComplete(resultsRef.current, Math.round((Date.now() - t0Ref.current) / 1000)); return; }
    onSaveAndExit?.({ quizType: "antonym", currentIndex: saveIdx, results: resultsRef.current, words: validWords, startTime: t0Ref.current, savedAt: Date.now(), expiresAt: new Date().setHours(23,59,59,999) });
  }

  function handleSelect(i) {
    if (selected !== null) return;
    setSelected(i);
    resultsRef.current = [...resultsRef.current, { word: validWords[idx], correct: currentChoices.options[i].correct }];
  }

  function next() {
    const nextIdx = idx + 1;
    if (nextIdx >= validWords.length) {
      onComplete(resultsRef.current, Math.round((Date.now() - t0Ref.current) / 1000));
    } else {
      setSelected(null);
      setCurrentChoices(buildChoices(validWords[nextIdx], validWords));
      setIdx(nextIdx);
    }
  }

  if (phase === "loading") return (
    <div className="mq-screen">
      <div className="mq-body" style={{ textAlign: "center" }}>
        <div className="mq-loading">🔄 Loading antonyms quiz…</div>
      </div>
    </div>
  );

  if (phase === "insufficient") return (
    <div className="mq-screen">
      <div className="mq-body" style={{ textAlign: "center" }}>
        <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>📚</div>
        <div style={{ fontWeight: 700, marginBottom: "1.25rem" }}>
          Not enough words with antonyms found — try adding more words to your list! 📚
        </div>
        <button className="next-btn" onClick={() => onComplete([], 0)}>← Back</button>
      </div>
    </div>
  );

  if (phase === "error") return (
    <div className="mq-screen">
      <div className="mq-body" style={{ textAlign: "center" }}>
        <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>😕</div>
        <div style={{ fontWeight: 700, marginBottom: "1.25rem" }}>{errorMsg}</div>
        <button className="next-btn" onClick={() => onComplete([], 0)}>← Back</button>
      </div>
    </div>
  );

  const QUIZ_COUNT = validWords.length;
  const pct = Math.round((idx / QUIZ_COUNT) * 100);
  const word = validWords[idx];

  return (
    <div className="mq-screen">
      <div className="test-prog-bar"><div className="test-prog-fill" style={{ width: `${pct}%` }} /></div>
      <div className="mq-body">
        <div className="test-ctr">Question {idx + 1} of {QUIZ_COUNT}</div>
        <div style={{ textAlign: "center", fontWeight: 700, fontSize: ".85rem", color: "var(--blue)", marginBottom: ".5rem", textTransform: "uppercase", letterSpacing: ".06em" }}>Antonyms Quiz</div>
        <div className="mq-word">{word.word}</div>
        <div className="mq-prompt">Which word means the OPPOSITE of: <strong>{word.word}</strong>?</div>
        {currentChoices && (
          <>
            <div className="mq-choices">
              {currentChoices.options.map((opt, i) => {
                let cls = "mq-choice";
                if (selected !== null) {
                  if (opt.correct) cls += " reveal";
                  else if (i === selected && !opt.correct) cls += " wrong";
                }
                return (
                  <button key={i} className={cls} disabled={selected !== null} onClick={() => handleSelect(i)}>
                    {opt.text}
                  </button>
                );
              })}
            </div>
            {selected !== null && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: ".75rem", width: "100%" }}>
                <div className={`fb-banner ${currentChoices.options[selected].correct ? "ok" : "bad"}`} style={{ width: "100%" }}>
                  {currentChoices.options[selected].correct ? "✅ Correct!" : "❌ The correct answer is highlighted above"}
                </div>
                <button className="next-btn" onClick={next}>
                  {idx + 1 >= QUIZ_COUNT ? "See Results →" : "Next Word →"}
                </button>
              </div>
            )}
          </>
        )}
        <div style={{ display: "flex", gap: ".5rem", justifyContent: "center", marginTop: "1.25rem" }}>
          <button style={{ background: "var(--amber)", color: "#fff", border: "none", borderRadius: 20, padding: ".3rem .8rem", fontSize: ".73rem", fontWeight: 700, cursor: "pointer" }}
            onClick={handleSaveAndExit}>💾 Save & Exit</button>
          <button style={{ background: "transparent", color: "var(--muted)", border: "1px solid var(--border)", borderRadius: 20, padding: ".3rem .8rem", fontSize: ".73rem", fontWeight: 600, cursor: "pointer" }}
            onClick={() => onExit?.()}>✕ Exit</button>
        </div>
      </div>
    </div>
  );
}
